import React, { useState, useMemo } from 'react';
import { IncomeBlock, IncomeRow, LegLine, PerformancePeriod } from '../../types/performance';
import StrategyBadge from '../common/StrategyBadge';
import {
  fmtUsd, fmtUsdShortSigned, fmtPct, pnlClass, fmtMonth, fmtDay, fmtDayShort, fmtExpiry,
} from '../../utils/performanceFormat';

type SortColumn = 'period' | 'stock_pnl' | 'option_pnl' | 'dividends' | 'total_pnl' | 'return' | 'benchmark_return' | 'alpha';
type SortDirection = 'asc' | 'desc';

interface MonthlyPnLTableProps {
  income: IncomeBlock | null;
  selectedPeriod: PerformancePeriod;
  showBenchmark?: boolean;
}

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

/**
 * The Income table: one row per day / week / month of the window, each
 * figure as served by gold (sums of gold.portfolio_daily; return and SPY
 * chained over the row's sessions; the footer = the period's KPI row).
 * Sorting is the only thing done here.
 */
const MonthlyPnLTable: React.FC<MonthlyPnLTableProps> = ({ income, selectedPeriod, showBenchmark = false }) => {
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({ column: 'period', direction: 'desc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const rows = useMemo(() => {
    const list = [...(income?.rows || [])];
    list.sort((a, b) => {
      if (sort.column === 'period') {
        return sort.direction === 'asc' ? a.period_start.localeCompare(b.period_start) : b.period_start.localeCompare(a.period_start);
      }
      const av = a[sort.column as keyof IncomeRow] as number | null;
      const bv = b[sort.column as keyof IncomeRow] as number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // unknown last
      if (bv == null) return -1;
      return sort.direction === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [income, sort]);

  const handleSort = (column: SortColumn) => {
    setSort(prev => ({ column, direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc' }));
  };
  const indicator = (column: SortColumn) => (sort.column !== column ? '⇅' : sort.direction === 'asc' ? '↑' : '↓');
  const money = (v: number | null) => (isMobile ? fmtUsdShortSigned(v) : fmtUsd(v));

  if (!income || rows.length === 0) {
    return (
      <div className="position-pnl-table-empty">
        <p>No P&L data available for the selected period ({selectedPeriod})</p>
      </div>
    );
  }

  const agg = income.aggregation;
  const periodLabel = agg === 'monthly' ? 'Month' : agg === 'daily' ? 'Day' : 'Week';
  const rowLabel = (r: IncomeRow) => (
    agg === 'monthly' ? fmtMonth(r.period_start, isMobile)
      : agg === 'daily' ? fmtDay(r.period_start)
        : `${fmtDayShort(r.period_start)} - ${fmtDayShort(r.period_end)}`
  );
  const t = income.totals;

  const legRow = (row: IncomeRow, p: LegLine) => (
    <tr key={`${row.period_start}-${p.position_id}`} className="contract-row">
      <td className="contract-cell">
        <span className="contract-label">
          {(p.contracts ?? 1) > 1 && `${p.contracts}x • `}
          {p.symbol} • {p.strike != null ? `$${p.strike.toFixed(2)}` : '—'} • {fmtExpiry(p.expiry)}
        </span>
        <StrategyBadge positionType={p.position_type ?? undefined} compact />
        {p.status && p.status !== 'active' && <span className={`status-badge status-${p.status}`}>{p.status}</span>}
      </td>
      <td className={`numeric-cell ${pnlClass(p.stock_pnl)}`}>{money(p.stock_pnl)}</td>
      <td className={`numeric-cell ${pnlClass(p.option_pnl)}`}>{money(p.option_pnl)}</td>
      <td className={`numeric-cell ${pnlClass(p.dividends)}`}>{money(p.dividends)}</td>
      <td className={`numeric-cell ${pnlClass(p.total_pnl)}`}>{money(p.total_pnl)}</td>
      <td className={`numeric-cell ${pnlClass(p.return)}`}>{fmtPct(p.return)}</td>
      {showBenchmark && <td className="numeric-cell"></td>}
      {showBenchmark && !isMobile && <td className="numeric-cell"></td>}
    </tr>
  );

  return (
    <div className="position-pnl-table-container">
      <table className="position-pnl-table">
        <thead>
          <tr>
            <th className="sortable-header" onClick={() => handleSort('period')}>{periodLabel} {indicator('period')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('stock_pnl')}>{isMobile ? 'Stock' : 'Stock P&L'} {indicator('stock_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('option_pnl')}>{isMobile ? 'Option' : 'Option P&L'} {indicator('option_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('dividends')}>{isMobile ? 'Div' : 'Dividend P&L'} {indicator('dividends')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('total_pnl')}>{isMobile ? 'Total' : 'Total P&L'} {indicator('total_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('return')} title="Time-weighted return of the period's sessions">{isMobile ? 'Ret%' : 'Return'} {indicator('return')}</th>
            {showBenchmark && (
              <th className="sortable-header numeric-column" onClick={() => handleSort('benchmark_return')}>SPY {indicator('benchmark_return')}</th>
            )}
            {showBenchmark && !isMobile && (
              <th className="sortable-header numeric-column" onClick={() => handleSort('alpha')}>Alpha {indicator('alpha')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const hasPositions = row.positions && row.positions.length > 0;
            const isOpen = expanded.has(row.period_start);
            return (
              <React.Fragment key={row.period_start}>
                <tr
                  className={`ticker-row ${isOpen ? 'expanded' : ''} ${hasPositions ? '' : 'no-expand'}`}
                  onClick={hasPositions ? (e) => toggle(row.period_start, e) : undefined}
                  style={hasPositions ? { cursor: 'pointer' } : undefined}
                >
                  <td className="ticker-cell">
                    {hasPositions && <span className="expand-icon">{isOpen ? '▼' : '▶'}</span>}
                    {rowLabel(row)}
                    {hasPositions && row.positions.length > 1 && <span className="contract-count">({row.positions.length})</span>}
                  </td>
                  <td className={`numeric-cell ${pnlClass(row.stock_pnl)}`}>{money(row.stock_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(row.option_pnl)}`}>{money(row.option_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(row.dividends)}`}>{money(row.dividends)}</td>
                  <td className={`numeric-cell ${pnlClass(row.total_pnl)}`}>{money(row.total_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(row.return)}`}>{fmtPct(row.return)}</td>
                  {showBenchmark && <td className={`numeric-cell ${pnlClass(row.benchmark_return)}`}>{fmtPct(row.benchmark_return)}</td>}
                  {showBenchmark && !isMobile && <td className={`numeric-cell ${pnlClass(row.alpha)}`}>{fmtPct(row.alpha)}</td>}
                </tr>
                {isOpen && row.positions.map(p => legRow(row, p))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="summary-row">
            <td className="summary-label">
              Total ({rows.length} {agg === 'monthly' ? 'months' : agg === 'daily' ? 'days' : 'weeks'})
              {t.annualised && <small title="A year or longer: returns per year (GIPS)"> · returns /yr</small>}
            </td>
            <td className={`numeric-cell summary-value ${pnlClass(t.stock_pnl)}`}>{money(t.stock_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.option_pnl)}`}>{money(t.option_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.dividends)}`}>{money(t.dividends)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.total_pnl)}`}>{money(t.total_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.return)}`}>{fmtPct(t.return)}</td>
            {showBenchmark && <td className={`numeric-cell summary-value ${pnlClass(t.benchmark_return)}`}>{fmtPct(t.benchmark_return)}</td>}
            {showBenchmark && !isMobile && <td className={`numeric-cell summary-value ${pnlClass(t.alpha)}`}>{fmtPct(t.alpha)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default MonthlyPnLTable;
